/**
 * API Client Configuration
 * Axios instance with interceptors for auth and token refresh
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://zsaas-qdzs.onrender.com';

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const state = useAuthStore.getState();
      const accessToken = state?.accessToken;
      const tokenType = state?.tokenType || 'Bearer';
      
      // Debug logging
      console.log('API Request:', config.url);
      console.log('Auth State:', {
        accessToken: accessToken ? `${accessToken.substring(0, 20)}...` : 'null',
        tokenType,
        isAuthenticated: state?.isAuthenticated,
        user: state?.user?.email
      });
      
      if (accessToken && config.headers) {
        config.headers.Authorization = `${tokenType} ${accessToken}`;
        console.log('Authorization header set:', `${tokenType} ${accessToken.substring(0, 20)}...`);
      } else {
        console.warn('No access token available for request:', config.url);
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle 401 and refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Skip refresh for login, signup, and refresh-token endpoints
    const skipRefreshPaths = ['/api/login', '/api/signup', '/api/refresh-token'];
    const shouldSkipRefresh = skipRefreshPaths.some(path => 
      originalRequest?.url?.includes(path)
    );
    
    if (error.response?.status === 401 && !originalRequest._retry && !shouldSkipRefresh) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const { refreshToken, logout, updateAccessToken } = useAuthStore.getState();
      
      if (!refreshToken) {
        logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      try {
        const response = await axios.post(`${API_BASE_URL}/api/refresh-token`, {
          refreshToken,
        });
        
        const { accessToken: newAccessToken } = response.data;
        updateAccessToken(newAccessToken);
        
        processQueue(null, newAccessToken);
        
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper to extract error message from API responses
export const getApiErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return axiosError.response?.data?.message || axiosError.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export default apiClient;
